import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { conditionLabel, conditionOf } from '@/lib/conversation-model';
import {
  callCostLabel,
  decisionOriginLabel,
  stepCapLabel,
  turnOutcomeLabel,
} from '@/lib/session-labels';
import type { SessionTurn } from '@matter_extractor/shared';
import { LayerLine, TurnLayerShell } from './layer-shell';
import type { ConversationSources } from './sources';

/**
 * The Moteur layer of one turn (§8.3 to §8.5, §12.3) - and the LAST of the five.
 *
 * « Un seul bandeau et pas deux : la décision du tour N est le chapeau de la
 * question N+1 » - so it is drawn at the foot of the turn it closes, between the
 * reply it was taken on and the question it produced. That is the whole of
 * `TURN_LAYER_ORDER`, and it is why this file exists apart from the four above
 * it even though it draws the least.
 *
 * ## « Ce qui l'a produite » is a NAME and not a category
 *
 * §8.7 records « la règle, la condition ou le garde-fou qui l'a produite », and
 * a screen that printed « condition de sortie » would have answered the category
 * and dropped the fact. So a condition is found back in the FROZEN protocol and
 * named by its rank in its step, with the triggers that made it true; a
 * guardrail is named by what Réglages asks the scribe to detect. The implicit
 * logic alone has no identifier, « parce qu'elle n'est écrite nulle part »
 * (§8.3), and the phrase says so rather than leaving a blank.
 *
 * ## Two directives that look alike and are not
 *
 * `turn.directive` is what the interviewer was TOLD before writing this turn's
 * message; `decision.directive` is what this turn decided for the NEXT one
 * (§8.7). Reading one turn therefore tells what the interviewer knew and what
 * the engine answered - which is exactly the pair somebody debugging a bad
 * question is looking for, and exactly the pair a single « directive » line
 * would have merged into a lie.
 *
 * The injected one is only announced as SERVED when a turn came after it. A
 * conversation can close on the very turn that wrote a directive - a guardrail,
 * a cap, a person who leaves - and « servie pour le tour N+1 » under a turn N
 * that is the last would name a turn that never happened.
 *
 * ## The cap is a line of its own, and only when there is one
 *
 * §8.3 gives a step three ceilings and says the three « valent exactement
 * plafond atteint », so the outcome alone never tells which one closed a step -
 * a production step went out on its fifth turn of twelve with no empty answer,
 * and the budget of §11.2 had to be deduced by elimination (issue #97). The
 * turn now records it, and this layer names it beside the decision rather than
 * inside it: what decided and what it was up against are two facts, and a
 * decision that reached no cap says nothing at all rather than « aucun ».
 *
 * On the BUDGET it also prints the step's cost as this turn's snapshot recorded
 * it. The budget a session was played against is frozen nowhere (§11.2 keeps it
 * in `model_params`, which an operator moves), so « budget IA de l'étape » on
 * its own is a ceiling whose value the reader would go and read as it stands
 * TODAY - and the Appels layer, two folds up, already prints the live one
 * beside the same cumulated cost. The recorded figure is the half that cannot
 * drift, and it is what makes the line hold on its own.
 */

export interface LayerMoteurProps {
  turn: SessionTurn;
  /** 1-based number of this turn - the directive is served to the NEXT one. */
  number: number;
  sources: ConversationSources;
  open: boolean;
  onToggle: () => void;
}

export function LayerMoteur({ turn, number, sources, open, onToggle }: LayerMoteurProps) {
  const { decision } = turn;
  const origin = decision.producedBy;
  const cap = stepCapLabel(decision.cap);
  const condition = conditionOf(sources.detail.cascade.protocol, origin.id);
  const served = number < sources.detail.turns.length;
  const guardrail =
    origin.kind === 'guardrail'
      ? sources.guardrails.find((entry) => entry.id === origin.id)
      : undefined;

  return (
    <TurnLayerShell
      layer="moteur"
      hint={`${turnOutcomeLabel(decision.outcome.kind)} · ${
        condition === null ? decisionOriginLabel(origin) : `condition ${condition.index}`
      }`}
      open={open}
      onToggle={onToggle}
    >
      <div
        data-testid={SESSIONS_TEST_IDS.turnEngine}
        className="flex flex-col gap-2 rounded-r-md border-bleu-nuit border-l-3 bg-gray-50 p-3"
      >
        <p className="font-semibold text-noir">
          Décision : {turnOutcomeLabel(decision.outcome.kind)}
          {decision.outcome.kind === 'go-to-step'
            ? ` ${sources.names.step(decision.outcome.stepId)}`
            : ''}
        </p>

        <dl className="flex flex-col gap-2">
          <LayerLine label="Produite par">
            <span
              className={`inline-flex items-center rounded px-2 py-0.5 font-semibold text-xs ${
                origin.kind === 'condition'
                  ? 'bg-bleu-nuit-tint text-bleu-nuit'
                  : origin.kind === 'guardrail'
                    ? 'bg-jaune-tint text-jaune-deep'
                    : 'bg-gray-100 text-gray-700'
              }`}
            >
              {decisionOriginLabel(origin)}
            </span>{' '}
            {condition === null ? null : (
              <span>
                n° {condition.index} de l’étape {condition.stepIndex} · {condition.stepName} -{' '}
                <span className="text-gray-600">
                  {conditionLabel(condition.condition, sources.names)}
                </span>
              </span>
            )}
            {guardrail === undefined ? null : (
              <span className="text-gray-600">{guardrail.detection}</span>
            )}
            {condition === null && guardrail === undefined && origin.id !== null ? (
              // The identifier is the only honest answer left: the frozen
              // protocol no longer carries the condition, or Réglages no longer
              // carries the guardrail, and pretending it was the implicit logic
              // would rewrite what the turn recorded.
              <span className="break-all font-mono text-gray-500 text-xs">{origin.id}</span>
            ) : null}
            {origin.kind === 'implicit' ? (
              <span className="text-gray-600">
                - aucune condition déclenchée, les règles générales de l’étape ont tranché (§8.3)
              </span>
            ) : null}
          </LayerLine>

          {cap === null ? null : (
            <LayerLine label="Plafond atteint">
              <span className="font-semibold text-jaune-deep">{cap}</span>
              <span className="text-gray-600">
                {' '}
                - l’étape était au plafond quand cette décision a été prise (§8.3)
                {decision.cap === 'budget'
                  ? `, à ${callCostLabel(turn.snapshot.stepCostEur)} cumulés sur l’étape`
                  : ''}
              </span>
            </LayerLine>
          )}

          {decision.invalidated.length === 0 ? null : (
            <LayerLine label="Objectifs invalidés">
              {decision.invalidated
                .map((objectiveId) => sources.names.objective(objectiveId))
                .join(', ')}
              <span className="text-gray-600">
                {' '}
                - matière vivante remise à zéro, extraits gardés et marqués (§8.4)
              </span>
            </LayerLine>
          )}

          {turn.directive === null ? null : (
            <LayerLine label="Directive reçue">
              <q>{turn.directive}</q>
              <span className="text-gray-600"> - servie avant le message de ce tour</span>
            </LayerLine>
          )}

          {decision.directive === null ? null : (
            <LayerLine label="Directive injectée">
              <span className="text-bleu-nuit-deep">
                <q>{decision.directive}</q>
              </span>
              <span className="text-gray-600">
                {' '}
                {served
                  ? `- servie à l’interviewer pour le tour ${number + 1}`
                  : '- jamais servie : la conversation s’est arrêtée sur ce tour'}
              </span>
            </LayerLine>
          )}

          {decision.outcome.kind === 'close-experience' ? (
            <LayerLine label="Texte de clôture">
              <q>{decision.outcome.text}</q>
            </LayerLine>
          ) : null}
        </dl>

        <p className="text-gray-500 text-xs">
          Ce que l’interviewer a reçu mot pour mot - les objectifs ouverts, la phrase « il manque
          encore », les règles globales - est dans la couche Brut : le dispositif ne demande aucune
          intention au modèle (H32, §8.7).
        </p>
      </div>
    </TurnLayerShell>
  );
}
